import { Avatar, Badge, Flex, Table, Text } from '@buffer-mono/popcorn'

import {
  AccessLevelSelect,
  COMMUNITY_TOOLTIP,
  ProductHeaderLabel,
  PUBLISH_TOOLTIP,
} from '../../../Team/components/ChannelPermissions'
import type {
  AccessLevel,
  ProductAccessLevels,
  ProductKey,
} from '../../../Team/types'
import { getDisplayName } from '../../../Team/utils/getDisplayName'
import type { TeamMemberForChannel } from '../../hooks/useTeamMembersForChannel'

import styles from './EditChannelPermissionsDialog.module.css'

import type { JSX } from 'react'

interface ChannelMemberPermissionsTableProps {
  members: TeamMemberForChannel[]
  /** Per-member levels keyed by account id. */
  levelsByMember: Map<string, ProductAccessLevels>
  onChange: (accountId: string, product: ProductKey, level: AccessLevel) => void
}

/**
 * The per-product, channel-scoped permissions table. This is the inverted view
 * of the team-member edit modal: rows are team members and the two columns are
 * the Publish + Community products for the one channel being edited. The owner
 * row is rendered but its selects are disabled (the owner always has access).
 */
export const ChannelMemberPermissionsTable = ({
  members,
  levelsByMember,
  onChange,
}: ChannelMemberPermissionsTableProps): JSX.Element => {
  return (
    <Table stickyHeader maxHeight={320} className={styles.permissionsTable}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell type="column" className={styles.stickyHeaderCell}>
            <Text weight="bold">Team Members · {members.length}</Text>
          </Table.HeaderCell>
          <Table.HeaderCell
            type="column"
            className={`${styles.productCol} ${styles.stickyHeaderCell}`}
          >
            <ProductHeaderLabel label="Publish" tooltip={PUBLISH_TOOLTIP} />
          </Table.HeaderCell>
          <Table.HeaderCell
            type="column"
            className={`${styles.productCol} ${styles.stickyHeaderCell}`}
          >
            <ProductHeaderLabel label="Community" tooltip={COMMUNITY_TOOLTIP} />
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {members.map((item) => {
          const { teamMember } = item
          const displayName = getDisplayName(teamMember)
          const levels = levelsByMember.get(teamMember.id) ?? {
            publish: 'none',
            comments: 'none',
          }
          return (
            <Table.Row key={teamMember.id}>
              <Table.Cell className={styles.memberNameCell}>
                <Flex align="center" gap="sm" className={styles.memberCell}>
                  <Avatar
                    src={teamMember.account?.avatar}
                    alt={displayName}
                    size="small"
                    className={styles.memberAvatar}
                  />
                  <Flex direction="column" className={styles.memberText}>
                    <Flex align="center" gap="xs">
                      <Text className={styles.memberName}>{displayName}</Text>
                      {teamMember.isOwner && (
                        <Badge size="small" color="brand">
                          Owner
                        </Badge>
                      )}
                      {teamMember.organizationRole === 'admin' &&
                        !teamMember.isOwner && (
                          <Badge size="small" color="subtle">
                            Admin
                          </Badge>
                        )}
                    </Flex>
                    <Text
                      size="sm"
                      color="subtle"
                      className={styles.memberName}
                    >
                      {teamMember.email}
                    </Text>
                  </Flex>
                </Flex>
              </Table.Cell>
              <Table.Cell className={styles.productCol}>
                <AccessLevelSelect
                  product="publish"
                  value={levels.publish}
                  onChange={(level): void =>
                    onChange(teamMember.id, 'publish', level)
                  }
                  aria-label={`Publish access for ${displayName}`}
                  disabled={teamMember.isOwner}
                  fullWidth
                />
              </Table.Cell>
              <Table.Cell className={styles.productCol}>
                <AccessLevelSelect
                  product="comments"
                  value={levels.comments}
                  onChange={(level): void =>
                    onChange(teamMember.id, 'comments', level)
                  }
                  aria-label={`Community access for ${displayName}`}
                  disabled={teamMember.isOwner}
                  fullWidth
                />
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}
